import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LockOutlined, PhoneOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerAuth } from '@features/customer-reservations/CustomerAuthContext';
import {
  useLoginWithPinMutation,
  useRequestPinResetMutation,
} from '@features/customer-reservations/queries';
import { loginLookup, LoginLookupEntry } from '@features/customer-reservations/api';
import { FormattedMessage, useIntl } from 'react-intl';

const PRIMARY = '#D02964';

type Props = {
  open: boolean;
  onClose: () => void;
  /** userId of the hairdresser profile being viewed – used to pre-filter matches */
  profileUserId?: string;
  /** Called after a successful login (auth state already refreshed) */
  onSuccess?: () => void;
};

const CustomerLoginDialog = ({ open, onClose, profileUserId, onSuccess }: Props) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { login } = useCustomerAuth();

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formInfo, setFormInfo] = useState<string | null>(null);
  const [step, setStep] = useState<'phone' | 'pin'>('phone');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [hairdressers, setHairdressers] = useState<LoginLookupEntry[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedHairdresserName, setSelectedHairdresserName] = useState('');

  const { mutateAsync: loginMutate } = useLoginWithPinMutation();
  const [isPending, setIsPending] = useState(false);
  const { mutate: requestReset, isPending: resetPending } = useRequestPinResetMutation();

  const resetState = () => {
    setPhone('');
    setPin('');
    setFormError(null);
    setFormInfo(null);
    setStep('phone');
    setLookupLoading(false);
    setHairdressers([]);
    setSelectedUserId('');
    setSelectedHairdresserName('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handlePhoneNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const normalizedPhone = phone.trim().replace(/[\s\-().+]/g, '');
    if (!normalizedPhone) {
      setFormError(intl.formatMessage({ id: 'customerLogin.phoneRequired' }));
      return;
    }

    setLookupLoading(true);
    try {
      const results = await loginLookup(normalizedPhone);
      const candidates = profileUserId ? results.filter(r => r.userId === profileUserId) : results;

      if (candidates.length === 0) {
        setFormError(intl.formatMessage({ id: 'customerLogin.phoneNotRegistered' }));
      } else if (candidates.length === 1) {
        setSelectedUserId(candidates[0].userId);
        setSelectedHairdresserName(candidates[0].hairdresserName);
        setStep('pin');
      } else {
        setHairdressers(candidates);
        setStep('pin');
      }
    } catch {
      setFormError(intl.formatMessage({ id: 'customerLogin.lookupFailed' }));
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    if (isPending) {
      return;
    }
    e.preventDefault();
    setFormError(null);

    if (!selectedUserId) {
      setFormError(intl.formatMessage({ id: 'customerLogin.selectHairdresserError' }));
      return;
    }
    if (!pin.trim()) {
      setFormError(intl.formatMessage({ id: 'customerLogin.pinRequired' }));
      return;
    }

    setIsPending(true);
    try {
      const response = await loginMutate({
        phone: phone.trim().replace(/[\s\-().+]/g, ''),
        userId: selectedUserId,
        pin: pin.trim(),
      });
      const session = await login();
      if (!session.valid || session.userId !== selectedUserId || session.clientId !== response.clientId) {
        setFormError(intl.formatMessage({
          id: 'customerLogin.sessionFailed',
          defaultMessage: 'Přihlášení se nepodařilo dokončit. Povolte cookies pro tento web a zkuste to znovu. Pokud používáte prohlížeč v jiné aplikaci, otevřete odkaz v běžném prohlížeči.',
        }));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['reservationServices'] });
      await queryClient.invalidateQueries({ queryKey: ['validateSession'] });
      await queryClient.invalidateQueries({ queryKey: ['customerAvailableSlots'] });
      await queryClient.invalidateQueries({ queryKey: ['myVisits'] });
      resetState();
      onSuccess?.();
      onClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : intl.formatMessage({ id: 'customerLogin.loginFailed' }));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <FormattedMessage id="customerLogin.title" />
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <FormattedMessage id="customerLogin.description" />
        </Typography>

        {formError && (
          <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        {formInfo && (
          <Alert severity="success" onClose={() => setFormInfo(null)} sx={{ mb: 2 }}>
            {formInfo}
          </Alert>
        )}

        {step === 'phone' && (
          <form onSubmit={handlePhoneNext}>
            <Stack spacing={2}>
              <TextField
                label={intl.formatMessage({ id: 'customerLogin.phone' })}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                type="tel"
                autoComplete="tel"
                inputMode="numeric"
                fullWidth
                autoFocus
                disabled={lookupLoading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={lookupLoading}
                startIcon={
                  lookupLoading ? <CircularProgress size={20} color="inherit" /> : undefined
                }
                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#b0234f' }, borderRadius: '12px' }}
              >
                <FormattedMessage id="common.continue" />
              </Button>
            </Stack>
          </form>
        )}

        {step === 'pin' && (
          <form onSubmit={handlePinSubmit}>
            <Stack spacing={2}>
              {hairdressers.length > 1 && !selectedUserId && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="customerLogin.selectHairdresser" />
                  </Typography>
                  <List dense disablePadding>
                    {hairdressers.map(h => (
                      <ListItemButton
                        key={h.userId}
                        selected={selectedUserId === h.userId}
                        onClick={() => {
                          setSelectedUserId(h.userId);
                          setSelectedHairdresserName(h.hairdresserName);
                        }}
                        sx={{
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 0.5,
                        }}
                      >
                        <ListItemText primary={h.hairdresserName} />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}

              {selectedHairdresserName && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  <FormattedMessage
                    id="customerLogin.enterPinFor"
                    values={{ name: selectedHairdresserName }}
                  />
                </Typography>
              )}

              <TextField
                label="PIN"
                value={pin}
                onChange={e => setPin(e.target.value)}
                type="password"
                autoComplete="current-password"
                inputMode="numeric"
                fullWidth
                autoFocus
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isPending}
                startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#b0234f' }, borderRadius: '12px' }}
              >
                <FormattedMessage id="customerLogin.login" />
              </Button>
              <Button
                variant="text"
                disabled={!selectedUserId || resetPending}
                onClick={() =>
                  requestReset(
                    { phone: phone.trim().replace(/[\s\-().+]/g, ''), userId: selectedUserId },
                    {
                      onSuccess: data => {
                        setFormError(null);
                        setFormInfo(data.message);
                      },
                      onError: error =>
                        setFormError(
                          error.message ||
                            intl.formatMessage({ id: 'customerLogin.resetLinkFailed' }),
                        ),
                    },
                  )
                }
              >
                <FormattedMessage id="customerLogin.forgotPin" />
              </Button>
              <Box textAlign="center">
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setStep('phone');
                    setHairdressers([]);
                    setSelectedUserId('');
                    setSelectedHairdresserName('');
                  }}
                >
                  <FormattedMessage id="common.back" />
                </Button>
              </Box>
            </Stack>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerLoginDialog;
